import React, { useEffect, useRef, useState, useCallback } from "react";
import API from "../utils/api";

function distMeters(aLat, aLon, bLat, bLon) {
  const R = 6371000;
  const rad = d => d * Math.PI / 180;
  const dLat = rad(bLat - aLat);
  const dLon = rad(bLon - aLon);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calcKm(points) {
  let meters = 0;
  let prev = null;
  for (const p of points) {
    if (p.accuracy && Number(p.accuracy) > 300) continue;
    if (prev) {
      const d = distMeters(Number(prev.latitude), Number(prev.longitude), Number(p.latitude), Number(p.longitude));
      if (d >= 5 && d <= 50000) meters += d;
    }
    prev = p;
  }
  return Math.round((meters / 1000) * 100) / 100;
}

function fmtTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
}

function fmtDate(d) {
  if (!d) return "";
  const [y, m, day] = String(d).slice(0, 10).split("-");
  return `${day}/${m}/${y}`;
}

function resolvePhoto(url) {
  if (!url) return "";
  const apiBase = String(API.defaults.baseURL || "/api");
  const apiOrigin = /^https?:/i.test(apiBase) ? apiBase.replace(/\/api\/?$/, "") : "";
  try {
    if (/^https?:/i.test(url)) {
      const parsed = new URL(url);
      const localHost = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1"
        || /^\d+\.\d+\.\d+\.\d+$/.test(parsed.hostname);
      if (localHost) return `${apiOrigin}${parsed.pathname}`;
      return url;
    }
  } catch { /* fall through */ }
  const pathname = url.startsWith("/") ? url : `/${url}`;
  return `${apiOrigin}${pathname}`;
}

async function loadVisitPhotos(list) {
  const urls = [];
  const visits = await Promise.all(list.map(async (visit) => {
    if (!visit?.id || !visit.photo_url) return visit;
    try {
      const res = await API.get(`/visits/${visit.id}/photo`, { responseType: "blob" });
      const blob = res.data;
      if (!blob || blob.size === 0 || (blob.type && blob.type.includes("json"))) return visit;
      const objectUrl = URL.createObjectURL(blob);
      urls.push(objectUrl);
      return { ...visit, photoSrc: objectUrl };
    } catch {
      return { ...visit, photoSrc: resolvePhoto(visit.photo_url) };
    }
  }));
  return { visits, urls };
}

function visitTime(v) {
  return v.created_at || v.check_in_time || v.date;
}

function sortVisits(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(visitTime(a)).getTime();
    const tb = new Date(visitTime(b)).getTime();
    if (ta !== tb) return ta - tb;
    return (Number(a.visit_order) || 0) - (Number(b.visit_order) || 0);
  });
}

const VISIT_COLORS = { hot: "#dc2626", warm: "#f59e0b", cold: "#3b82f6", default: "#8b5cf6" };

let leafletLoadPromise = null;
function loadLeaflet() {
  if (leafletLoadPromise) return leafletLoadPromise;
  leafletLoadPromise = new Promise((resolve, reject) => {
    if (window.L) { resolve(window.L); return; }
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return leafletLoadPromise;
}

function numberedIcon(L, n, color, extra) {
  return L.divIcon({
    html: `<div style="background:${color};color:#fff;border-radius:8px;min-width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);padding:0 4px">${extra || n}</div>`,
    className: "", iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

export default function FieldDayMap({ userId, userName, date, accent = "#E31E24", onClose }) {
  const mapRef     = useRef(null);
  const mapObjRef  = useRef(null);
  const [trail, setTrail]     = useState([]);
  const [visits, setVisits]   = useState([]);
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selVisit, setSelVisit] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoUrlsRef = useRef([]);

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const displayDate = date || today;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trailRes, visitsRes, checkinRes] = await Promise.all([
        API.get(`/locations/trail/${userId}?date=${displayDate}`).catch(() => ({ data: [] })),
        API.get(`/visits/team?date=${displayDate}&from=${displayDate}&to=${displayDate}`),
        API.get(`/checkins/report?user_id=${userId}&from=${displayDate}&to=${displayDate}`).catch(() => ({ data: [] })),
      ]);
      setTrail(trailRes.data || []);
      const dayVisits = sortVisits((visitsRes.data || []).filter(v => String(v.user_id) === String(userId)));
      const loaded = await loadVisitPhotos(dayVisits);
      photoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      photoUrlsRef.current = loaded.urls;
      setVisits(loaded.visits);
      setCheckin((checkinRes.data || [])[0] || null);
    } catch {
      setError("Failed to load field day data");
    }
    setLoading(false);
  }, [userId, displayDate]);

  useEffect(() => {
    fetchData();
    return () => {
      photoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      photoUrlsRef.current = [];
    };
  }, [fetchData]);

  const visitPts = visits.filter(v => v.latitude && v.longitude);
  const trailPts = trail.filter(p => p.latitude && p.longitude);
  // Where HRMS recorded the login and log-off. Route km runs login → visits → log-off.
  const loginLat = checkin?.login_latitude ?? checkin?.latitude;
  const loginLng = checkin?.login_longitude ?? checkin?.longitude;
  const loginPt = loginLat != null && loginLng != null ? { latitude: loginLat, longitude: loginLng } : null;
  const logoffPt = checkin?.check_out_time && checkin?.logoff_latitude != null && checkin?.logoff_longitude != null
    ? { latitude: checkin.logoff_latitude, longitude: checkin.logoff_longitude } : null;
  const hasMap = visitPts.length > 0 || trailPts.length > 0 || !!loginPt || !!logoffPt;

  useEffect(() => {
    if (loading || !mapRef.current || !hasMap) return;

    loadLeaflet().then(L => {
      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }

      const map = L.map(mapRef.current);
      mapObjRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      const bounds = [];

      if (trailPts.length > 1) {
        const latlngs = trailPts.map(p => [Number(p.latitude), Number(p.longitude)]);
        L.polyline(latlngs, { color: accent, weight: 3, opacity: 0.35 }).addTo(map);
        latlngs.forEach(p => bounds.push(p));
      }

      const routeLine = [loginPt, ...visitPts, logoffPt].filter(Boolean).map(p => [Number(p.latitude), Number(p.longitude)]);
      if (routeLine.length > 1) {
        L.polyline(routeLine, { color: accent, weight: 2, opacity: 0.7, dashArray: "6 6" }).addTo(map);
      }
      const pinIcon = (text, bg) => L.divIcon({
        html: `<div style="background:${bg};color:#fff;border-radius:8px;padding:3px 7px;font-size:10px;font-weight:900;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);white-space:nowrap">${text}</div>`,
        className: "", iconSize: [60, 22], iconAnchor: [30, 11],
      });
      if (loginPt) {
        L.marker([Number(loginPt.latitude), Number(loginPt.longitude)], { icon: pinIcon("Login", "#16a34a") })
          .addTo(map).bindPopup(`<b>Login</b><br>${fmtTime(checkin.check_in_time)}`);
        bounds.push([Number(loginPt.latitude), Number(loginPt.longitude)]);
      }
      if (logoffPt) {
        const lastKnown = checkin.logoff_source === "last_known";
        L.marker([Number(logoffPt.latitude), Number(logoffPt.longitude)], { icon: pinIcon(lastKnown ? "Log-off*" : "Log-off", "#475569") })
          .addTo(map).bindPopup(`<b>Log-off</b><br>${fmtTime(checkin.check_out_time)}${lastKnown ? "<br>Last known position — no fix at log-off" : ""}`);
        bounds.push([Number(logoffPt.latitude), Number(logoffPt.longitude)]);
      }

      if (visitPts.length > 0) {
        const instLine = visitPts.map(v => [Number(v.latitude), Number(v.longitude)]);
        L.polyline(instLine, { color: accent, weight: 5, opacity: 0.9 }).addTo(map);
        instLine.forEach(p => bounds.push(p));

        const start = visitPts[0];
        const finish = visitPts[visitPts.length - 1];
        const startIcon = L.divIcon({
          html: `<div style="background:${accent};color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">S</div>`,
          className: "", iconSize: [30, 30], iconAnchor: [15, 15],
        });
        L.marker([Number(start.latitude), Number(start.longitude)], { icon: startIcon })
          .addTo(map)
          .bindPopup(`<b>Start</b><br>${start.institution_name}<br>${fmtTime(visitTime(start))}`);

        if (visitPts.length > 1) {
          const endIcon = L.divIcon({
            html: `<div style="background:#1e293b;color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">F</div>`,
            className: "", iconSize: [30, 30], iconAnchor: [15, 15],
          });
          L.marker([Number(finish.latitude), Number(finish.longitude)], { icon: endIcon })
            .addTo(map)
            .bindPopup(`<b>Finish</b><br>${finish.institution_name}<br>${fmtTime(visitTime(finish))}`);
        }

        visitPts.forEach((v) => {
          const n = visits.indexOf(v) + 1;
          const color = VISIT_COLORS[v.lead_status] || VISIT_COLORS.default;
          const photo = v.photoSrc || "";
          const popup = `<b>${n}. ${v.institution_name}</b><br>` +
            `${fmtTime(visitTime(v))} · ${v.lead_status || ""}<br>` +
            `${Number(v.latitude).toFixed(5)}, ${Number(v.longitude).toFixed(5)}` +
            (photo ? `<br><img src="${photo}" alt="" style="width:160px;margin-top:6px;border-radius:8px" />` : "");
          L.marker([Number(v.latitude), Number(v.longitude)], { icon: numberedIcon(L, n, color) })
            .addTo(map)
            .bindPopup(popup);
        });
      }

      if (bounds.length) map.fitBounds(L.latLngBounds(bounds).pad(0.2));
    }).catch(() => setError("Could not load map library"));

    return () => {
      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
    };
  }, [loading, hasMap, trail, visits, checkin, accent]); // eslint-disable-line

  // Same straight-line route the server stores: login → visits → log-off.
  const routeKm = calcKm([loginPt, ...visitPts, logoffPt].filter(Boolean));
  const trailKm = calcKm(trailPts);
  const storedKm = checkin?.route_km ?? checkin?.total_km_travelled;
  const displayKm = storedKm != null && Number(storedKm) > 0
    ? Number(storedKm).toFixed(1)
    : (routeKm || 0).toFixed(1);
  const checkinTime = checkin?.check_in_time ? fmtTime(checkin.check_in_time) : "—";
  const checkoutTime = checkin?.check_out_time ? fmtTime(checkin.check_out_time) : "Active";
  const startName = visitPts[0]?.institution_name;
  const finishName = visitPts.length > 1 ? visitPts[visitPts.length - 1]?.institution_name : startName;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:20, width:"min(96vw,820px)", maxHeight:"92vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", justifyContent:"space-between", background:accent, color:"#fff" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>Field day — {userName}</div>
            <div style={{ fontSize:12, opacity:0.9 }}>{fmtDate(displayDate)} · In {checkinTime} → {checkoutTime}</div>
            {startName && (
              <div style={{ fontSize:11, opacity:0.9, marginTop:4 }}>
                Start: {startName}{finishName && finishName !== startName ? ` · Finish: ${finishName}` : ""}
              </div>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:22, fontWeight:900 }}>{displayKm}</div>
              <div style={{ fontSize:10, opacity:0.85 }} title="Login → every visit → log-off, straight line">ROUTE KM</div>
              {trailKm > 0 && <div style={{ fontSize:9, opacity:0.75 }}>GPS trail {trailKm.toFixed(1)}</div>}
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:22, fontWeight:900 }}>{visits.length}</div>
              <div style={{ fontSize:10, opacity:0.85 }}>VISITS</div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close map" style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:50, width:32, height:32, color:"#fff", fontSize:18, cursor:"pointer" }}>×</button>
          </div>
        </div>

        <div style={{ flex:1, overflow:"auto", display:"flex", flexDirection:"column" }}>
          {loading && <div style={{ padding:60, textAlign:"center", color:"#94a3b8" }}>Loading field day…</div>}
          {!loading && error && <div style={{ padding:60, textAlign:"center", color:"#dc2626" }}>{error}</div>}

          {!loading && !error && (
            <>
              {hasMap ? (
                <div ref={mapRef} style={{ height:340, width:"100%", background:"#f1f5f9" }} />
              ) : (
                <div style={{ padding:28, textAlign:"center", color:"#64748b", background:"#f8fafc", borderBottom:"1px solid #e2e8f0" }}>
                  <div style={{ fontWeight:700, color:"#1e293b", marginBottom:4 }}>No GPS pins on these visits</div>
                  <div style={{ fontSize:12 }}>Photos still appear below when the rep saved them with the visit.</div>
                </div>
              )}

              <div style={{ padding:"16px 20px" }}>
                <div style={{ fontSize:14, fontWeight:800, color:"#1e293b", marginBottom:12 }}>
                  Institutions ({visits.length}) — numbered in visit order
                </div>
                {visits.length === 0 && <div style={{ color:"#94a3b8", fontSize:13 }}>No visit entries logged for this day</div>}
                {visits.map((v, i) => {
                  const color = VISIT_COLORS[v.lead_status] || VISIT_COLORS.default;
                  const isSelected = selVisit === v.id;
                  const photo = v.photoSrc || "";
                  const isStart = i === 0;
                  const isFinish = i === visits.length - 1 && visits.length > 1;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setSelVisit(isSelected ? null : v.id)}
                      style={{ border:`1.5px solid ${isSelected ? color : "#e2e8f0"}`, borderRadius:12, padding:"12px 14px", marginBottom:10, cursor:"pointer", background: isSelected ? color + "0d" : "#fff" }}
                    >
                      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                        {photo ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPhotoPreview({ src: photo, name: v.institution_name }); }}
                            style={{ padding:0, border:"none", background:"none", cursor:"pointer", flexShrink:0 }}
                          >
                            <img src={photo} alt={v.institution_name} style={{ width:64, height:64, objectFit:"cover", borderRadius:10, display:"block" }} />
                          </button>
                        ) : (
                          <div style={{ width:64, height:64, borderRadius:10, background:"#f1f5f9", color:"#94a3b8", fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>No photo</div>
                        )}
                        <div style={{ background:color, color:"#fff", borderRadius:8, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:13, flexShrink:0 }}>{i + 1}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>
                            {v.institution_name}
                            {isStart && <span style={{ marginLeft:6, fontSize:10, color:accent }}>START</span>}
                            {isFinish && <span style={{ marginLeft:6, fontSize:10, color:"#1e293b" }}>FINISH</span>}
                          </div>
                          <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
                            {fmtTime(visitTime(v))}
                            {v.city ? ` · ${v.city}` : ""}
                            {v.companions?.length ? ` · With ${v.companions.map(person => person.name).join(", ")}` : " · Solo"}
                          </div>
                          <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
                            <span style={{ background: color + "20", color, borderRadius:100, padding:"2px 8px", fontWeight:700, marginRight:6 }}>{(v.lead_status || "warm").toUpperCase()}</span>
                            {v.latitude && v.longitude
                              ? `${Number(v.latitude).toFixed(5)}, ${Number(v.longitude).toFixed(5)}`
                              : "GPS not captured"}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #e2e8f0", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:12 }}>
                          {v.contact_name  && <div><span style={{ color:"#94a3b8" }}>Contact:</span> <b>{v.contact_name}</b></div>}
                          {v.contact_phone && <div><span style={{ color:"#94a3b8" }}>Phone:</span> <b>{v.contact_phone}</b></div>}
                          {v.lead_stage    && <div><span style={{ color:"#94a3b8" }}>Stage:</span> <b>{v.lead_stage}</b></div>}
                          {v.visit_purpose && <div><span style={{ color:"#94a3b8" }}>Purpose:</span> <b>{v.visit_purpose}</b></div>}
                          {v.comments && <div style={{ gridColumn:"1/-1", background:"#f8fafc", borderRadius:8, padding:"8px 10px", color:"#475569" }}>{v.comments}</div>}
                          {v.latitude && v.longitude && (
                            <div style={{ gridColumn:"1/-1" }}>
                              <a href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`} target="_blank" rel="noopener noreferrer" style={{ color: accent, fontSize:12, fontWeight:600, textDecoration:"none" }}>
                                Open this pin in Google Maps
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMap && visitPts.length > 1 && (
                  <a
                    href={`https://www.google.com/maps/dir/${visitPts.map(v => `${v.latitude},${v.longitude}`).join("/")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display:"inline-block", marginTop:4, padding:"8px 12px", borderRadius:10, background:accent, color:"#fff", fontSize:12, fontWeight:700, textDecoration:"none" }}
                  >
                    Open start → finish route in Google Maps
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {photoPreview && (
        <button
          type="button"
          onClick={() => setPhotoPreview(null)}
          style={{ position:"fixed", inset:0, zIndex:9100, background:"rgba(0,0,0,0.82)", border:"none", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"zoom-out" }}
        >
          <img src={photoPreview.src} alt={photoPreview.name} style={{ maxWidth:"92vw", maxHeight:"82vh", borderRadius:12, objectFit:"contain" }} />
          <div style={{ color:"#fff", marginTop:12, fontWeight:700 }}>{photoPreview.name}</div>
        </button>
      )}
    </div>
  );
}
